<template>
  <div class="mt-3 attack-events-table">
    <h6>{{ $t('AttackEvents') }}</h6>

    <a-alert v-if="error"
             type="error"
             show-icon
             :message="$t('message.UnableToFetchAttackInformation')"/>

    <p v-else-if="!loading && !tableData.length" class="font-small text-muted">
      {{ $t('message.NoAttackDataForPeriod') }}
    </p>

    <a-table v-else
             :columns="columns"
             :data-source="tableData"
             :loading="loading"
             rowKey="key"
             bordered>
      <template slot="duration" slot-scope="text, record">
        {{ record.duration_display }}
        <a-tag v-if="record.ongoing" color="red" class="ml-1">{{ $t('Ongoing') }}</a-tag>
      </template>
    </a-table>
  </div>
</template>

<script>
import moment from 'moment';

// SNI-type (platform-wide) events carry no clients count — sort those rows
// below any real count instead of producing NaN comparisons.
function clientsSortValue(row) {
  return row.clients === null || row.clients === undefined ? -1 : row.clients;
}

export default {
  props: {
    events: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    error: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      columns: [
        {
          title: this.$t('Domain'),
          dataIndex: 'domain_display',
          key: 'domain',
          sorter: (a, b) => String(a.domain_display).localeCompare(String(b.domain_display)),
        },
        {
          title: this.$t('Type'),
          dataIndex: 'type',
          key: 'type',
          sorter: (a, b) => String(a.type).localeCompare(String(b.type)),
        },
        {
          title: this.$t('AttackTraffic'),
          dataIndex: 'requests',
          key: 'requests',
          sorter: (a, b) => a.requests - b.requests,
        },
        {
          title: this.$t('PeakRequest'),
          dataIndex: 'peak_requests',
          key: 'peak_requests',
          sorter: (a, b) => a.peak_requests - b.peak_requests,
        },
        {
          title: this.$t('Clients'),
          dataIndex: 'clients_display',
          key: 'clients',
          sorter: (a, b) => clientsSortValue(a) - clientsSortValue(b),
        },
        {
          title: this.$t('Started'),
          dataIndex: 'started_display',
          key: 'started_at',
          sorter: (a, b) => moment(a.started_at).valueOf() - moment(b.started_at).valueOf(),
        },
        {
          title: this.$t('Duration'),
          dataIndex: 'duration_seconds',
          key: 'duration_seconds',
          sorter: (a, b) => a.duration_seconds - b.duration_seconds,
          scopedSlots: {customRender: 'duration'},
        },
      ],
    };
  },
  computed: {
    tableData: function () {
      return this.events.map((event, index) => {
        return {
          ...event,
          key: index,
          // SNI-type events are platform-wide: the error log carries no
          // domain field, so the API returns domain: null.
          domain_display: event.domain ? event.domain : this.$t('AllDomainsPlatform'),
          clients_display: event.clients === null || event.clients === undefined
            ? '-'
            : event.clients,
          started_display: this.formatStarted(event.started_at),
          duration_display: this.humanizeDuration(event.duration_seconds),
        };
      });
    },
  },
  methods: {
    formatStarted(startedAt) {
      if (!startedAt) {
        return '-';
      }

      return moment(startedAt).format('YYYY-MM-DD HH:mm:ss');
    },
    humanizeDuration(seconds) {
      if (seconds === null || seconds === undefined || seconds === '') {
        return '-';
      }

      const total = Math.floor(Number(seconds));

      if (isNaN(total) || total < 0) {
        return '-';
      }

      if (total < 60) {
        return `${total}s`;
      }

      const units = [
        {label: 'd', size: 86400},
        {label: 'h', size: 3600},
        {label: 'm', size: 60},
        {label: 's', size: 1},
      ];

      const parts = [];
      let remainder = total;

      for (const unit of units) {
        const value = Math.floor(remainder / unit.size);
        remainder = remainder % unit.size;

        if (value > 0) {
          parts.push(`${value}${unit.label}`);
        }
      }

      // keep it readable: only the two most significant units
      return parts.slice(0, 2).join(' ');
    },
  },
};
</script>
